// Campaign Analysis platform membership.
//
// A media platform belongs to the selected campaign only when the governed
// approved-campaign platform identity says so. A connected integration, a provider that
// can report, and a live provider signal are *capabilities*: they may enrich the state of
// a platform campaign scope has already admitted, but none of them admits a platform on
// its own.
//
//   connected integration  ≠ selected campaign platform
//   reporting capability   ≠ selected campaign platform
//   live provider signal   ≠ selected campaign platform
//
// The scope fails closed. When the approved-campaign platform set cannot be resolved the
// scope admits nothing, so the page reports a truthful "no approved platforms" state
// instead of manufacturing membership from every supported/connected platform.

export const SHERLOC_CAMPAIGN_PLATFORM_SCOPE_UNAVAILABLE =
  "campaign_platform_scope_unavailable";

export const SHERLOC_CAMPAIGN_PLATFORM_SCOPE_APPROVED = "approved_campaign_platform_scope";

const defaultNormalizeKey = (rawKey) => String(rawKey ?? "").trim().toLowerCase();

export const buildSherlocCampaignPlatformScope = ({
  approvedPlatformKeys = [],
  normalizeKey,
} = {}) => {
  const normalize = typeof normalizeKey === "function" ? normalizeKey : defaultNormalizeKey;
  const keys = new Set();

  (Array.isArray(approvedPlatformKeys) ? approvedPlatformKeys : []).forEach((rawKey) => {
    const key = normalize(rawKey);
    if (key) keys.add(key);
  });

  return {
    keys,
    resolved: keys.size > 0,
    reason: keys.size
      ? SHERLOC_CAMPAIGN_PLATFORM_SCOPE_APPROVED
      : SHERLOC_CAMPAIGN_PLATFORM_SCOPE_UNAVAILABLE,
    normalizeKey: normalize,
  };
};

export const isSherlocCampaignScopedPlatformKey = (scope, rawKey) => {
  if (!scope?.resolved) return false;
  const normalize = typeof scope.normalizeKey === "function" ? scope.normalizeKey : defaultNormalizeKey;
  const key = normalize(rawKey);
  return Boolean(key) && scope.keys.has(key);
};

export const filterSherlocCampaignScopedPlatforms = (scope, platforms = []) =>
  (Array.isArray(platforms) ? platforms : []).filter((platform) =>
    isSherlocCampaignScopedPlatformKey(scope, platform?.key || platform?.platform || platform?.label)
  );
